import { Share as ShareIcon } from "@mui/icons-material";
import { Grid } from "@mui/material";
import {
  addDays,
  endOfDay,
  endOfWeek,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfWeek,
  subHours,
} from "date-fns";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetAbsenceRequestsForCalendarQuery,
  useGetPendingAbsenceRequestsCountQuery,
  useGetPendingAbsenceRequestsQuery,
} from "../../api/absenceRequestsApi";
import { useGetAuditLogsQuery } from "../../api/auditApi";
import { useGetShareLinksQuery } from "../../api/shareApi";
import {
  useGetBillingDetailsQuery,
  useGetSubscriptionsListQuery,
} from "../../api/subscriptionApi";
import { useAppSelector } from "../../app/hooks";
import {
  AbsenceIcon,
  AuditIcon,
  PaymentIcon,
} from "../../components/common/pageIcons";
import { MenuItemBase } from "../../components/layout/navMenu";
import { AbsenceRequestStatus } from "../../constants/absenceEnums";
import { UserRoles } from "../../constants/roles";
import { hasSubscriptionAccess } from "../../constants/subscriptionLevels";
import HubGroupPanel, { HubPreviewRow } from "./components/HubGroupPanel";
import HubOverviewSection from "./components/HubOverviewSection";
import { formatShortDate, hasRole, hasShortcut } from "./hubUtils";

interface AdminHubOverviewProps {
  shortcuts: MenuItemBase[];
  subscriptionLevel: string | null;
}

const overlapsDay = (start: Date, end: Date, day: Date) =>
  isWithinInterval(day, { start: startOfDay(start), end: endOfDay(end) });

const overlapsRange = (
  start: Date,
  end: Date,
  rangeStart: Date,
  rangeEnd: Date,
) => start <= rangeEnd && end >= rangeStart;

const AdminHubOverview: React.FC<AdminHubOverviewProps> = ({
  shortcuts,
  subscriptionLevel,
}) => {
  const navigate = useNavigate();
  const role = useAppSelector((state) => state.auth.user)?.role;
  const now = useMemo(() => new Date(), []);
  const today = startOfDay(now);
  const weekStart = startOfWeek(today);
  const weekEnd = endOfWeek(today);
  const auditFrom = subHours(now, 24).toISOString();
  const auditTo = now.toISOString();

  const canAbsences =
    hasShortcut(shortcuts, "/admin/absences") &&
    hasRole(role, [UserRoles.ADMIN]) &&
    hasSubscriptionAccess(subscriptionLevel, "Standard");
  const canSubscription = hasShortcut(shortcuts, "/subscriptions");
  const canShare = hasShortcut(shortcuts, "/admin/share-links");
  const canAudit =
    hasShortcut(shortcuts, "/audit") &&
    hasRole(role, [UserRoles.ADMIN, UserRoles.SUPER_ADMIN]);

  const {
    data: pendingCount = 0,
    isLoading: pendingCountLoading,
    isError: pendingCountError,
  } = useGetPendingAbsenceRequestsCountQuery(undefined, { skip: !canAbsences });
  const { data: pendingRequests, isLoading: pendingListLoading } =
    useGetPendingAbsenceRequestsQuery(
      { pageNumber: 1, pageSize: 20 },
      { skip: !canAbsences },
    );
  const { data: calendarRequests = [], isLoading: calendarLoading } =
    useGetAbsenceRequestsForCalendarQuery(
      {
        startDate: weekStart.toISOString(),
        endDate: weekEnd.toISOString(),
        status: AbsenceRequestStatus.Approved,
      },
      { skip: !canAbsences },
    );

  const {
    data: billing,
    isLoading: billingLoading,
    isError: billingError,
  } = useGetBillingDetailsQuery(undefined, { skip: !canSubscription });
  const { data: subscriptionsList = [], isLoading: listLoading } =
    useGetSubscriptionsListQuery(undefined, { skip: !canSubscription });

  const {
    data: shareLinks = [],
    isLoading: shareLoading,
    isError: shareError,
  } = useGetShareLinksQuery(undefined, { skip: !canShare });

  const {
    data: auditPage,
    isLoading: auditLoading,
    isError: auditError,
  } = useGetAuditLogsQuery(
    { from: auditFrom, to: auditTo, page: 1, pageSize: 5 },
    { skip: !canAudit },
  );

  const outToday = useMemo(
    () =>
      calendarRequests.filter((request) =>
        overlapsDay(
          parseISO(request.startDate),
          parseISO(request.endDate),
          today,
        ),
      ),
    [calendarRequests, today],
  );
  const outThisWeek = useMemo(() => {
    const employees = new Set<number>();
    for (const request of calendarRequests) {
      if (
        overlapsRange(
          parseISO(request.startDate),
          parseISO(request.endDate),
          weekStart,
          weekEnd,
        )
      ) {
        employees.add(request.employeeId);
      }
    }
    return employees.size;
  }, [calendarRequests, weekEnd, weekStart]);

  const absenceRows = useMemo<HubPreviewRow[]>(() => {
    const pendingRows = (pendingRequests?.items ?? []).map((request) => ({
      id: `abs-pending-${request.id}`,
      title: request.employeeName || "Employee",
      subtitle: `${request.absenceCategoryName} · ${formatShortDate(request.startDate) ?? ""}${
        request.endDate !== request.startDate
          ? `–${formatShortDate(request.endDate) ?? ""}`
          : ""
      }`,
      meta: "Pending",
      tone: "warning" as const,
      onClick: () => navigate("/admin/absences?tab=pending"),
    }));
    const outRows = outToday.map((request) => ({
      id: `abs-out-${request.id}`,
      title: request.employeeName || "Employee",
      subtitle: request.absenceCategoryName,
      meta: "Out today",
      tone: "info" as const,
      onClick: () => navigate("/admin/absences?tab=calendar"),
    }));
    return [...pendingRows, ...outRows];
  }, [navigate, outToday, pendingRequests?.items]);

  const currentListItem = subscriptionsList.find(
    (item) => item.subscriptionId === billing?.subscription?.id,
  );
  const vehicleQuantity = billing?.subscription?.vehicleQuantity ?? 0;
  const syncedCount = currentListItem?.syncedVehicleCount ?? 0;
  const unsynced = Math.max(0, vehicleQuantity - syncedCount);
  const planName = billing?.subscriptionPlan?.name ?? "No plan";
  const planStatus = billing?.subscription?.status ?? "none";

  const subscriptionRows = useMemo<HubPreviewRow[]>(() => {
    if (!billing?.subscription) return [];
    const periodEnd = formatShortDate(billing.subscription.currentPeriodEnd);
    return [
      {
        id: "sub-plan",
        title: planName,
        subtitle: billing.cancelAtPeriodEnd
          ? "Cancels at period end"
          : `Status: ${planStatus}`,
        meta: periodEnd ? `Renews ${periodEnd}` : planStatus,
        tone: billing.cancelAtPeriodEnd
          ? "warning"
          : planStatus === "active" || planStatus === "trialing"
            ? "success"
            : "warning",
        onClick: () => navigate("/subscriptions"),
      },
      {
        id: "sub-vehicles",
        title: `${syncedCount} of ${vehicleQuantity} vehicles synced`,
        subtitle:
          unsynced > 0
            ? `${unsynced} still waiting to sync`
            : "Vehicle quantity matches synced units",
        meta: unsynced > 0 ? "Gap" : "In sync",
        tone: unsynced > 0 ? "warning" : "success",
        onClick: () => navigate("/subscriptions"),
      },
    ];
  }, [
    billing,
    navigate,
    planName,
    planStatus,
    syncedCount,
    unsynced,
    vehicleQuantity,
  ]);

  const expiringSoon = useMemo(() => {
    const cutoff = addDays(now, 7);
    return shareLinks.filter((link) => {
      if (!link.isActive || link.isExpired) return false;
      const expires = new Date(link.expiresAt);
      return expires <= cutoff;
    });
  }, [now, shareLinks]);
  const activeLinks = useMemo(
    () => shareLinks.filter((link) => link.isActive && !link.isExpired),
    [shareLinks],
  );

  const shareRows = useMemo<HubPreviewRow[]>(() => {
    return [...activeLinks]
      .sort(
        (a, b) =>
          new Date(a.expiresAt).getTime() - new Date(b.expiresAt).getTime(),
      )
      .map((link) => {
        const expiresSoon = expiringSoon.some((item) => item.id === link.id);
        const itemLabels = link.items.map((item) => item.label).join(", ");
        return {
          id: `share-${link.id}`,
          title: link.name?.trim() || "Untitled link",
          subtitle: itemLabels || "No assets listed",
          meta: `Expires ${formatShortDate(link.expiresAt) ?? ""}`,
          tone: expiresSoon ? "warning" : "success",
          onClick: () => navigate("/admin/share-links"),
        };
      });
  }, [activeLinks, expiringSoon, navigate]);

  const auditRows = useMemo<HubPreviewRow[]>(() => {
    return (auditPage?.data ?? []).map((log) => ({
      id: `audit-${log.id}`,
      title: log.actorName || log.actorType || "System",
      subtitle: [log.entityType, log.action].filter(Boolean).join(" · "),
      meta: formatShortDate(log.createdAt) ?? "",
      tone: "neutral",
      onClick: () => navigate("/audit"),
    }));
  }, [auditPage?.data, navigate]);

  const badges: Record<string, number> = {};
  if (canAbsences) {
    badges["/admin/absences"] = pendingCountError ? 0 : pendingCount;
  }
  if (canSubscription) badges["/subscriptions"] = unsynced;
  if (canShare) {
    badges["/admin/share-links"] = shareError ? 0 : expiringSoon.length;
  }
  if (canAudit) {
    badges["/audit"] = auditError ? 0 : auditPage?.totalRecords ?? 0;
  }

  return (
    <HubOverviewSection shortcuts={shortcuts} badges={badges}>
      {canAbsences && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<AbsenceIcon />}
            title="Absences"
            href="/admin/absences?tab=pending"
            hrefLabel="Pending"
            loading={
              pendingCountLoading || pendingListLoading || calendarLoading
            }
            emptyText="No pending requests and nobody is out today."
            counts={[
              {
                label: "pending",
                value: pendingCountError ? 0 : pendingCount,
                tone: "warning",
              },
              { label: "out today", value: outToday.length, tone: "info" },
              { label: "out this week", value: outThisWeek, tone: "neutral" },
            ]}
            rows={absenceRows}
          />
        </Grid>
      )}
      {canSubscription && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<PaymentIcon />}
            title="Subscription"
            href="/subscriptions"
            hrefLabel="Billing"
            loading={billingLoading || listLoading}
            emptyText="No billing information available."
            counts={[
              {
                label: "vehicles",
                value: billingError ? 0 : vehicleQuantity,
                tone: "neutral",
              },
              {
                label: "synced",
                value: billingError ? 0 : syncedCount,
                tone: unsynced > 0 ? "warning" : "success",
              },
              {
                label: "unsynced",
                value: billingError ? 0 : unsynced,
                tone: "warning",
              },
            ]}
            rows={subscriptionRows}
          />
        </Grid>
      )}
      {canShare && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<ShareIcon />}
            title="Share links"
            href="/admin/share-links"
            hrefLabel="Links"
            loading={shareLoading}
            emptyText="No active tracking links."
            counts={[
              {
                label: "active",
                value: shareError ? 0 : activeLinks.length,
                tone: "success",
              },
              {
                label: "expire in 7d",
                value: shareError ? 0 : expiringSoon.length,
                tone: "warning",
              },
            ]}
            rows={shareRows}
          />
        </Grid>
      )}
      {canAudit && (
        <Grid item xs={12} sm={6}>
          <HubGroupPanel
            icon={<AuditIcon />}
            title="Audit log"
            href="/audit"
            hrefLabel="Log"
            loading={auditLoading}
            emptyText="No audit events in the last 24 hours."
            counts={[
              {
                label: "last 24h",
                value: auditError ? 0 : auditPage?.totalRecords ?? 0,
                tone: "info",
              },
            ]}
            rows={auditRows}
          />
        </Grid>
      )}
    </HubOverviewSection>
  );
};

export default AdminHubOverview;
